import { DropdownOption } from '@src/CommonComponents/DynamicFilters';
import {
  GEO_SELECTED_REGION,
  GEO_SELECTED_STATES,
  GEO_SELECTED_MARKET,
  GEO_SELECTED_TINS,
  SESSION_MULTI_TINS_KEY,
  SESSION_STATE_KEY,
} from '@src/apps/CallSummary/CallsFilter/constants';

export interface SelectedCorporateTin {
  value: string;
  name?: string;
}

const parseJSON = <T extends unknown>(value: string | null, defaultValue: T): T => {
  if (!value) return defaultValue;
  try {
    return JSON.parse(value);
  } catch {
    return defaultValue;
  }
};

const getGeoOption = (key: string, returnFirst = false): DropdownOption | DropdownOption[] | null => {
  const parsed = parseJSON<DropdownOption[]>(sessionStorage.getItem(key), []);
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return returnFirst ? null : [];
  }
  return returnFirst ? (parsed[0] as DropdownOption) : parsed;
};

const setGeoOption = (key: string, value: DropdownOption | DropdownOption[]) => {
  const arr = Array.isArray(value) ? value : [value];
  sessionStorage.setItem(key, JSON.stringify(arr));
};

export const getMultiSelectedTins = (): string => 
  sessionStorage.getItem(SESSION_MULTI_TINS_KEY) || '';

export const isMultiTinJourney = (): boolean => !!getMultiSelectedTins();

export const getSessionState = (): string | null => 
  sessionStorage.getItem(SESSION_STATE_KEY);

export const getSessionRegionOption = () => getGeoOption(GEO_SELECTED_REGION, true);
export const setSessionRegion = (regions: DropdownOption[]) => setGeoOption(GEO_SELECTED_REGION, regions);

export const getSessionStateOption = () => getGeoOption(GEO_SELECTED_STATES);
export const setSessionStateOption = (states: DropdownOption[]) => setGeoOption(GEO_SELECTED_STATES, states);

export const getSessionMarketOption = () => getGeoOption(GEO_SELECTED_MARKET);
export const setSessionMarketOption = (markets: DropdownOption[]) => setGeoOption(GEO_SELECTED_MARKET, markets);

export const getSessionGeoTins = (): DropdownOption[] =>
  (getGeoOption(GEO_SELECTED_TINS) as DropdownOption[]) || [];
export const setSessionGeoTins = (tins: DropdownOption[]) =>
  setGeoOption(GEO_SELECTED_TINS, tins);

export const getSelectedCorporateTin = (): SelectedCorporateTin | null => 
  parseJSON<Record<string, unknown> extends unknown ? Record<string, unknown> : never>(getSessionState(), {})?.selectedCorporateTin as SelectedCorporateTin | null || null;

export const getSessionStorageFilters = (): Record<string, unknown> =>
  parseJSON<Record<string, unknown> extends unknown ? Record<string, unknown> : never>(getSessionState(), {});

const filterTaxIds = (value: DropdownOption[]) =>
  value.filter((option) => {
    const tinValue = option.id || option.value || (option as DropdownOption & { Tin?: string }).Tin;
    return tinValue !== 'ALL' && tinValue !== 'All';
  });

export function setSessionStorageFilters(
  key: string,
  value: DropdownOption | DropdownOption[]
) {
  const parsedState: Record<string, unknown> = parseJSON<Record<string, unknown> extends unknown ? Record<string, unknown> : never>(getSessionState(), {});

  if (key === 'region') {
    setSessionRegion(Array.isArray(value) ? value : [value]);
  } else if (key === 'state') {
    setSessionStateOption(Array.isArray(value) ? value : [value]);
  } else if (key === 'market') {
    setSessionMarketOption(Array.isArray(value) ? value : [value]);
  } else if (key === 'taxId' && Array.isArray(value)) {
    const filtered = filterTaxIds(value);
    if (isMultiTinJourney()) {
      const existing = (parsedState.journeyExtendedTinFilterSelected as Record<string, unknown>) || {};
      parsedState.journeyExtendedTinFilterSelected = {
        ...existing,
        [key]: filtered,
      };
    }
    parsedState[key] = filtered;
  } else if (Array.isArray(value)) {
    parsedState[key] = value.map((opt) => opt.value);
  } else {
    parsedState[key] = value.value;
  }

  sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(parsedState));
}
